/*
 * Copyright (c) 2015 2017, Massachusetts Institute of Technology (MIT)
 * All rights reserved.
 * ====================================================================
 * This material may be reproduced by or for the
 * U.S. Government pursuant to the copyright license
 * under the clause at DFARS 252.227 7013 (June, 1995).
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are permitted provided that the following conditions
 * are met:
 *
 * 1. Redistributions of source code must retain the above copyright
 * notice, this list of conditions and the following disclaimer.
 *
 * 2. Redistributions in binary form must reproduce the above copyright
 * notice, this list of conditions and the following disclaimer in the
 * documentation and/or other materials provided with the distribution.
 *
 * 3. Neither the name of the copyright holder nor the names of its
 * contributors may be used to endorse or promote products derived
 * from this software without specific prior written permission.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 * INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES
 * (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR
 * SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION)
 * HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE)
 * ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED
 * OF THE POSSIBILITY OF SUCH DAMAGE.
 * ====================================================================
 */
define((require) => {
  'use strict';

  // External dependencies
  const Colors = require('./constants/ColorModule');
  const ConversionFactors = require('./constants/ConversionModule');

  Colors.init();

  return {
    Colors,
    ConversionFactors,
    MILLIS_IN_HOUR: 3600000,
    MPH: 'MPH',
    KNOTS: 'KNOTS',
    WGS84: 'EPSG:4326',
    PSEUDO_MERCATOR: 'EPSG:3857',
    WIND_PROB_OFFSETS: [6, 12, 18, 24, 30, 36, 42, 48, 54, 60, 66, 72, 78, 84, 90, 96, 102, 108, 114, 120],
    poiDefaultTypes: ['OTHER', 'SURGE FLAG', 'WIND TIMING FLAG'],
    poiDefaultStyles: {
      color: `#000000`,
      font: `Arial`,
      fontSize: `12`,
      style: `Normal`, 
      fill: `#000000`,
      size: 6,
      zoom: 4
    },
    basemapList: [ 
      {
        value: 'streets-v2',
        name: 'Streets', 
        title: 'Streets', 
        description: 'Annotated map with street and highway details', 
        src:'images/streets.png',
        styles: {
          name:'default-graticule',
          width: 2,
          color:'rgba(242,234,194,0.8)',
          intervals:[90, 45, 30, 20, 10, 5],
          sld: 'cross_streets'
        }
      }, {
        value: 'satellite', 
        name: 'Satellite', 
        title: 'Imagery', 
        description: 'Annotated map with satellite imagery and aerial photography', 
        src:'images/satellite.png',
        styles: {
          name:'default-graticule',
          width: 3,
          color:'rgba(37,52,33,0.8)',
          intervals:[90, 45, 30, 20, 10, 5],
          sld: 'cross_hybrid'
        }
      }, {
        value: 'topo-v2', 
        name: 'Topo', 
        title: 'Topography', 
        description: 'High contrast map with emphasis on land use and topography', 
        src:'images/topo.png',
        styles: {
          name:'default-graticule',
          width: 2,
          color:'rgba(226,225,218,0.8)',
          intervals:[90, 45, 30, 20, 10, 5],
          sld: 'cross_topo'
        }
      }, {
        value: 'openstreetmap', 
        name: 'OSM', 
        title: 'Open Street Map', 
        description: 'All-purpose, medium contrast map with emphasis on roads and land use', 
        src:'images/openstreetmap.png', 
        styles: {
          name:'default-graticule',
          width: 2,
          color:'rgba(241,241,241,0.8)',
          intervals:[90, 45, 30, 20, 10, 5],
          sld: 'cross_pastel'
        }
      }, {
        value: 'backdrop', 
        name: 'Backdrop', 
        title: 'Light Gray Canvas', 
        description: 'A light background map', 
        src:'images/backdrop.png', 
        styles: {
          name:'default-graticule',
          width: 2,
          color:'rgba(241,242,240,0.8)',
          intervals:[90, 45, 30, 20, 10, 5],
          sld: 'cross_positron'
        }
      }, {
        value: 'basic-v2', 
        name: 'Basic', 
        title: 'Bright Color Canvas', 
        description: 'All-purpose map with bright colors and high land/water contrast', 
        src:'images/basic.png',
        styles: {
          name:'default-graticule',
          width: 2,
          color:'rgba(224,224,228,0.8)',
          intervals:[90, 45, 30, 20, 10, 5],
          sld: 'cross_basic'
        }
      }, {
        value: 'voyager-v2', 
        name: 'Voyager', 
        title: 'Light Color Canvas', 
        description: 'Default, all-purpose map with light colors and low contrast', 
        src:'images/voyager.png',
        styles: {
          name:'default-graticule',
          width: 2,
          color:'rgba(250,248,243,0.8)',
          intervals:[90, 45, 30, 20, 10, 5],
          sld: 'cross_voyager'
        }
      }, {
        value: 'classic', 
        name: 'Classic HURREVAC', 
        title: 'Classic', 
        description: 'A simple map in the style of NHC\'s graphics', 
        src:'images/classic.png', 
        styles: {
          name:'classic-graticule',
          width: 2,
          color:'rgba(190,190,190, 0.9)',
          intervals:[90, 45, 30, 20, 10, 5],
          sld: 'cross_classic'
        }
      }
    ],
    /**
     * Maps from forecast hour to error radius in nautical miles, used to produce error cones.
     *
     * The Atlantic rates were provided by NHC.
     * Sea Island verified the East Pacific rates back to 2008 using reports found at https://www.nhc.noaa.gov/verification/verify3.shtml. East Pacific for 2007, 2006, and 2005 use the 2008 rates.
     * CPHC has been inconsistent about updating its error rates over the years. In many years, they just used EPAC rates. The rates used reflect Sea Island's research of email archives.
     * JTWC has been inconsistent about updating error rates. The rates used reflect Sea Island's research of email archives.
     */
    errorConeRadii: {
      al: {
        default: new Map([[3, 16], [12, 25], [24, 39], [36, 49], [48, 62], [60, 77], [72, 95], [96, 134], [120, 200]]),
        2026: new Map([[3, 16], [12, 25], [24, 39], [36, 49], [48, 62], [60, 77], [72, 95], [96, 134], [120, 200]]),
        2025: new Map([[3, 16], [12, 26], [24, 39], [36, 52], [48, 67], [60, 83], [72, 100], [96, 142], [120, 213]]),
        2024: new Map([[3, 16], [12, 26], [24, 41], [36, 55], [48, 70], [60, 88], [72, 102], [96, 151], [120, 220]]),
        2023: new Map([[3, 16], [12, 26], [24, 39], [36, 53], [48, 67], [60, 81], [72, 99], [96, 145], [120, 205]]),
        2022: new Map([[3, 16], [12, 26], [24, 39], [36, 52], [48, 67], [60, 84], [72, 100], [96, 142], [120, 200]]),
        2021: new Map([[3, 16], [12, 27], [24, 40], [36, 55], [48, 69], [60, 86], [72, 102], [96, 148], [120, 200]]),
        2020: new Map([[3, 16], [12, 26], [24, 41], [36, 55], [48, 69], [72, 103], [96, 151], [120, 196]]),
        2019: new Map([[3, 16], [12, 26], [24, 41], [36, 54], [48, 68], [72, 102], [96, 151], [120, 198]]),
        2018: new Map([[3, 16], [12, 26], [24, 43], [36, 56], [48, 74], [72, 103], [96, 151], [120, 198]]),
        2017: new Map([[3, 16], [12, 29], [24, 45], [36, 63], [48, 78], [72, 107], [96, 159], [120, 211]]),
        2016: new Map([[3, 16], [12, 30], [24, 49], [36, 66], [48, 84], [72, 115], [96, 165], [120, 237]]),
        2015: new Map([[3, 16], [12, 32], [24, 52], [36, 71], [48, 90], [72, 122], [96, 170], [120, 225]]),
        2014: new Map([[3, 16], [12, 33], [24, 52], [36, 72], [48, 92], [72, 125], [96, 170], [120, 226]]),
        2013: new Map([[3, 16], [12, 33], [24, 52], [36, 72], [48, 92], [72, 128], [96, 177], [120, 229]]),
        2012: new Map([[3, 16], [12, 36], [24, 56], [36, 75], [48, 95], [72, 141], [96, 180], [120, 236]]),
        2011: new Map([[3, 16], [12, 36], [24, 59], [36, 79], [48, 98], [72, 144], [96, 190], [120, 239]]),
        2010: new Map([[3, 16], [12, 36], [24, 62], [36, 85], [48, 108], [72, 161], [96, 220], [120, 285]]),
        2009: new Map([[3, 16], [12, 36], [24, 62], [36, 89], [48, 111], [72, 167], [96, 230], [120, 302]]),
        2008: new Map([[3, 16], [12, 39], [24, 67], [36, 92], [48, 118], [72, 170], [96, 233], [120, 305]]),
        2007: new Map([[3, 16], [12, 39], [24, 69], [36, 99], [48, 124], [72, 179], [96, 252], [120, 326]]),
        2006: new Map([[3, 16], [12, 40], [24, 70], [36, 100], [48, 128], [72, 188], [96, 231], [120, 303]]),
        2005: new Map([[3, 16], [12, 44], [24, 77], [36, 111], [48, 143], [72, 208], [96, 266], [120, 357]]),
      },
      cp: {
        default: new Map([[3, 16], [12, 25], [24, 37], [36, 48], [48, 56], [60, 66], [72, 78], [96, 106], [120, 138]]),
        2026: new Map([[3, 16], [12, 25], [24, 37], [36, 48], [48, 56], [60, 66], [72, 78], [96, 106], [120, 138]]),
        2024: new Map([[3, 16], [12, 34], [24, 49], [36, 66], [48, 81], [60, 95], [72, 120], [96, 137], [120, 156]]),
        2023: new Map([[3, 16], [12, 28], [24, 44], [36, 61], [48, 78], [60, 95], [72, 111], [96, 138], [120, 162]]),
        2022: new Map([[3, 16], [12, 28], [24, 44], [36, 61], [48, 78], [60, 95], [72, 111], [96, 138], [120, 162]]),
        2021: new Map([[3, 16], [12, 28], [24, 44], [36, 61], [48, 78], [60, 95], [72, 111], [96, 138], [120, 162]]),
        2020: new Map([[3, 16], [12, 28], [24, 44], [36, 61], [48, 78], [72, 111], [96, 138], [120, 162]]),
        2019: new Map([[3, 16], [12, 31], [24, 48], [36, 62], [48, 79], [72, 120], [96, 163], [120, 233]]),
        2018: new Map([[3, 16], [12, 31], [24, 48], [36, 62], [48, 79], [72, 120], [96, 163], [120, 233]]),
        2017: new Map([[3, 16], [12, 31], [24, 48], [36, 62], [48, 79], [72, 120], [96, 163], [120, 233]]),
        2016: new Map([[3, 16], [12, 27], [24, 42], [36, 55], [48, 70], [72, 100], [96, 137], [120, 172]]),
        2015: new Map([[3, 16], [12, 26], [24, 42], [36, 54], [48, 69], [72, 100], [96, 143], [120, 182]]),
        2014: new Map([[3, 16], [12, 30], [24, 46], [36, 62], [48, 79], [72, 105], [96, 154], [120, 190]]),
        2013: new Map([[3, 16], [12, 30], [24, 49], [36, 66], [48, 82], [72, 111], [96, 157], [120, 197]]),
        2012: new Map([[3, 16], [12, 33], [24, 52], [36, 72], [48, 89], [72, 121], [96, 170], [120, 216]]),
        2011: new Map([[3, 16], [12, 33], [24, 59], [36, 79], [48, 98], [72, 134], [96, 187], [120, 230]]),
        2010: new Map([[3, 16], [12, 36], [24, 59], [36, 82], [48, 102], [72, 138], [96, 174], [120, 220]]),
        2009: new Map([[3, 16], [12, 36], [24, 59], [36, 85], [48, 105], [72, 148], [96, 187], [120, 230]]),
        2008: new Map([[3, 16], [12, 36], [24, 66], [36, 92], [48, 115], [72, 161], [96, 210], [120, 256]]),
        2007: new Map([[3, 16], [12, 36], [24, 66], [36, 92], [48, 115], [72, 161], [96, 210], [120, 256]]),
        2006: new Map([[3, 16], [12, 36], [24, 66], [36, 92], [48, 115], [72, 161], [96, 210], [120, 256]]),
        2005: new Map([[3, 16], [12, 36], [24, 66], [36, 92], [48, 115], [72, 161], [96, 210], [120, 256]]),
      },
      ep: {
        default: new Map([[3, 16], [12, 25], [24, 37], [36, 48], [48, 56], [60, 66], [72, 78], [96, 106], [120, 138]]),
        2026: new Map([[3, 16], [12, 25], [24, 37], [36, 48], [48, 56], [60, 66], [72, 78], [96, 106], [120, 138]]),
        2025: new Map([[3, 16], [12, 26], [24, 38], [36, 50], [48, 59], [60, 71], [72, 83], [96, 113], [120, 146]]),
        2024: new Map([[3, 16], [12, 26], [24, 39], [36, 53], [48, 65], [60, 76], [72, 92], [96, 119], [120, 152]]),
        2023: new Map([[3, 16], [12, 25], [24, 38], [36, 51], [48, 63], [60, 78], [72, 86], [96, 110], [120, 137]]),
        2022: new Map([[3, 16], [12, 25], [24, 38], [36, 51], [48, 65], [60, 79], [72, 93], [96, 120], [120, 146]]),
        2021: new Map([[3, 16], [12, 25], [24, 37], [36, 51], [48, 64], [60, 77], [72, 89], [96, 114], [120, 138]]),
        2020: new Map([[3, 16], [12, 25], [24, 38], [36, 51], [48, 65],  [72, 91], [96, 115], [120, 138]]),
        2019: new Map([[3, 16], [12, 25], [24, 38], [36, 48], [48, 62], [72, 88], [96, 115], [120, 145]]),
        2018: new Map([[3, 16], [12, 25], [24, 39], [36, 50], [48, 66], [72, 94], [96, 125], [120, 162]]),
        2017: new Map([[3, 16], [12, 25], [24, 40], [36, 51], [48, 66], [72, 93], [96, 116], [120, 151]]),
        2016: new Map([[3, 16], [12, 27], [24, 42], [36, 55], [48, 70], [72, 100], [96, 137], [120, 172]]),
        2015: new Map([[3, 16], [12, 26], [24, 42], [36, 54], [48, 69], [72, 100], [96, 143], [120, 182]]),
        2014: new Map([[3, 16], [12, 30], [24, 46], [36, 62], [48, 79], [72, 105], [96, 154], [120, 190]]),
        2013: new Map([[3, 16], [12, 30], [24, 49], [36, 66], [48, 82], [72, 111], [96, 157], [120, 197]]),
        2012: new Map([[3, 16], [12, 33], [24, 52], [36, 72], [48, 89], [72, 121], [96, 170], [120, 216]]),
        2011: new Map([[3, 16], [12, 33], [24, 59], [36, 79], [48, 98], [72, 134], [96, 187], [120, 230]]),
        2010: new Map([[3, 16], [12, 36], [24, 59], [36, 82], [48, 102], [72, 138], [96, 174], [120, 220]]),
        2009: new Map([[3, 16], [12, 36], [24, 59], [36, 85], [48, 105], [72, 148], [96, 187], [120, 230]]),
        2008: new Map([[3, 16], [12, 36], [24, 66], [36, 92], [48, 115], [72, 161], [96, 210], [120, 256]]),
        2007: new Map([[3, 16], [12, 36], [24, 66], [36, 92], [48, 115], [72, 161], [96, 210], [120, 256]]),
        2006: new Map([[3, 16], [12, 36], [24, 66], [36, 92], [48, 115], [72, 161], [96, 210], [120, 256]]),
        2005: new Map([[3, 16], [12, 36], [24, 66], [36, 92], [48, 115], [72, 161], [96, 210], [120, 256]]),
      },
      jtwc: {
        default: new Map([[3, 15], [12, 25], [24, 43], [36, 55], [48, 72], [72, 111], [96, 145], [120, 180]]),
        2024: new Map([[3, 15], [12, 25], [24, 43], [36, 55], [48, 72], [72, 111], [96, 145], [120, 180]]),
        2023: new Map([[3, 15], [12, 30], [24, 45], [36, 60], [48, 80], [72, 120], [96, 160], [120, 200]]),
        2022: new Map([[3, 15], [12, 35], [24, 50], [36, 65], [48, 80], [72, 120], [96, 165], [120, 225]]),
        2021: new Map([[3, 15], [12, 35], [24, 50], [36, 65], [48, 80], [72, 120], [96, 165], [120, 225]]),
        2020: new Map([[3, 15], [12, 35], [24, 50], [36, 65], [48, 80], [72, 120], [96, 165], [120, 225]]),
        2019: new Map([[3, 15], [12, 35], [24, 50], [36, 65], [48, 80], [72, 120], [96, 165], [120, 225]]),
        2018: new Map([[3, 15], [12, 35], [24, 50], [36, 65], [48, 80], [72, 120], [96, 165], [120, 225]]),
        2017: new Map([[3, 15], [12, 35], [24, 50], [36, 65], [48, 80], [72, 120], [96, 165], [120, 225]]),
        2016: new Map([[3, 15], [12, 23], [24, 56], [36, 73], [48, 96], [72, 140], [96, 195], [120, 259]]),
        2015: new Map([[3, 15], [12, 23], [24, 56], [36, 73], [48, 96], [72, 140], [96, 195], [120, 259]]),
        2014: new Map([[3, 15], [12, 40], [24, 65], [36, 90], [48, 115], [72, 165], [96, 220], [120, 290]]),
        2013: new Map([[3, 15], [12, 40], [24, 65], [36, 90], [48, 115], [72, 165], [96, 220], [120, 290]]),
        2012: new Map([[3, 15], [12, 40], [24, 65], [36, 90], [48, 115], [72, 165], [96, 220], [120, 290]]),
        2011: new Map([[3, 15], [12, 40], [24, 65], [36, 90], [48, 115], [72, 165], [96, 220], [120, 290]]),
        2010: new Map([[3, 15], [12, 40], [24, 65], [36, 90], [48, 115], [72, 165], [96, 220], [120, 290]]),
        2009: new Map([[3, 15], [12, 40], [24, 65], [36, 90], [48, 115], [72, 165], [96, 220], [120, 290]]),
        2008: new Map([[3, 15], [12, 40], [24, 65], [36, 90], [48, 115], [72, 165], [96, 220], [120, 290]]),
        2007: new Map([[3, 15], [12, 40], [24, 65], [36, 90], [48, 115], [72, 165], [96, 220], [120, 290]]),
        2006: new Map([[3, 15], [12, 40], [24, 65], [36, 90], [48, 115], [72, 165], [96, 220], [120, 290]]),
        2005: new Map([[3, 15], [12, 40], [24, 65], [36, 90], [48, 115], [72, 165], [96, 220], [120, 290]]),
      },
    },
    MOM_BASE_LAYERS: {   
      1: ['cat1_cat2_MOM_cog_inundation'],        
      2: ['cat1_cat2_MOM_cog_inundation'], 
      3: ['cat3_cat4_MOM_cog_inundation'],   
      4: ['cat3_cat4_MOM_cog_inundation'],     
      5: ['cat5_MOM_cog_inundation']     
    },
    EvacTiming: {
      SCENARIOS:'evacScenarios',
      MANUAL: 'manualTimes',
      ARCS: 'arcConfig'
    },
    WindSpeeds: {
      TROP_STORM_SPEED: 34,
      STRONG_STORM_SPEED: 50,
      HURRICANE_SPEED: 64
    },
    timeZoneRegion: {
      AST: 'America/Puerto_Rico',
      CDT: 'America/Chicago',
      CST: 'America/Belize',
      EDT: 'America/New_York',
      EST: 'America/Cancun',
      HST: 'Pacific/Honolulu',
      MDT: 'America/Denver',
      MST: 'America/Phoenix',
      PDT: 'America/Los_Angeles',
      PST: 'Asia/Manila',
      CVT: 'Atlantic/Cape_Verde',
      GMT: 'Atlantic/Azores'
    },
    days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    daysAbbr: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    months: ['January', 'February', 'March', 'April', 'May', 'June', 'July','August', 'September', 'October', 'November', 'December'],
    monthsAbbr: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    directions: ['S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW', 'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'PAR'],
    militaryAbbr: {
      '-12': 'Y',
      '-11': 'X',
      '-10': 'W',
      '-9': 'V',
      '-8': 'U',
      '-7': 'T',
      '-6': 'S',
      '-5': 'R',
      '-4': 'Q',
      '-3': 'P',
      '-2': 'O',
      '-1': 'N',
      '+0': 'Z',
      '+1': 'A',
      '+2': 'B',
      '+3': 'C',
      '+4': 'D',
      '+5': 'E',
      '+6': 'F',
      '+7': 'G',
      '+8': 'H',
      '+9': 'I',
      '+10': 'K',
      '+11': 'L',
      '+12': 'M',
      '+13': 'M',
      '+14': 'N'
    },
    States: {
      FL: 'Florida',
      GA: 'Georgia',
      SC: 'South Carolina',
      NC: 'North Carolina',
      VA: 'Virginia',
      DC: 'District of Columbia',
      MD: 'Maryland',
      DE: 'Delaware',
      NJ: 'New Jersey',
      PA: 'Pennsylvania',
      NY: 'New York',
      CT: 'Connecticut',
      RI: 'Rhode Island',
      MA: 'Massachusetts',
      VT: 'Vermont',
      NH: 'New Hampshire',
      ME: 'Maine',
      AL: 'Alabama',
      MS: 'Mississippi',
      LA: 'Louisiana',
      TX: 'Texas',
      OK: 'Oklahoma',
      AR: 'Arkansas',
      TN: 'Tennessee',
      KY: 'Kentucky',
      WV: 'West Virginia',
      HI: 'Hawaii',
      PR: 'Puerto Rico',
      VI: 'US Virgin Islands',
      CA: 'California',
      AZ: 'Arizona',
      NM: 'New Mexico',
      NV: 'Nevada',
      JA: 'Jamaica',
      MP: 'N Mariana Islands',
      GU: 'Guam',
      PW: 'Palau',
      MH: 'Marshall Islands',
      FM: 'Micronesia',
      BR: 'Bermuda',
      AS: 'American Samoa',
      UM: 'US Minor Islands'
    },
    rainHrsColorMap: {
      '20,200,250': { 
        value: 0.01,
        title: '0.01 to .1 in' 
      },  
      '61,133,198': { 
        value: 0.1,
        title: '0.1 to .25 in' 
      },   
      '0,20,50': { 
        value: 0.25,
        title: '0.25 to .5 in' 
      },  
      '0,250,20': { 
        value: 0.5,
        title: '0.5 to .75 in' 
      },    
      '0,160,15': { 
        value: 0.75,
        title: '0.75 to 1 in' 
      },  
      '0,100,10': { 
        value: 1,
        title: '1 to 1.5 in' 
      },   
      '250,250,0': { 
        value: 1.5,
        title: '1.5 to 2 in' 
      },   
      '255,217,102': { 
        value: 2,
        title: '2 to 2.5 in' 
      },  
      '250,150,0': { 
        value: 2.5,
        title: '2.5 to 3 in' },   
      '250,0,0': { 
        value: 3,
        title: '3 to 4 in' 
      },      
      '175,0,0': { 
        value: 4,
        title: '4 to 5 in' 
      },   
      '125,0,0': { 
        value: 5,
        title: '5 to 6 in' 
      },
      '250,0,250': { 
        value: 6,
        title: '6 to 8 in' 
      },
      '124,74,224': { 
        value: 8,
        title: '8 to 10 in' 
      }, 
      '219,219,219': { 
        value: 10,
        title: '10+ in' 
      }, 
      '125,125,125': { 
        value: 'No Data',
        title: 'No Data' 
      } 
    },
    rainDaysColorMap: {
      '20,200,250': { 
        value: 0.01,
        title: '0.01 to .1 in' 
      },  
      '61,133,198': { 
        value: 0.1,
        title: '0.1 to .25 in' 
      },   
      '0,20,50': { 
        value: 0.25,
        title: '0.25 to .5 in' 
      },  
      '0,250,20': { 
        value: 0.5,
        title: '0.5 to 1 in' 
      },    
      '0,160,15': {
        value: 1,
        title: '1 to 1.5 in' 
      },  
      '0,100,10': { 
        value: 1.5,
        title: '1 to 2 in' 
      },   
      '250,250,0': { 
        value: 2,
        title: '2 to 3 in' 
      },   
      '255,217,102': { 
        value: 3,
        title: '3 to 4 in' 
      },  
      '250,150,0': { 
        value: 4,
        title: '4 to 5 in' 
      },   
      '250,0,0': { 
        value: 5,
        title: '5 to 6 in' 
      },      
      '175,0,0': { 
        value: 6,
        title: '6 to 8 in' 
      },   
      '125,0,0': { 
        value: 8,
        title: '8 to 10 in' 
      },
      '250,0,250': { 
        value: 10,
        title: '10 to 15 in' 
      },
      '124,74,224': { 
        value: 15,
        title: '15 to 20 in' 
      },  
      '219,219,219': { 
        value: 20,
        title: '20+ in' 
      }, 
      '125,125,125': { 
        value: 'No Data',
        title: 'No Data' 
      } 
    }
  };
});
